import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';

import { FormattedMessage, defineMessages } from 'react-intl';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Icon from '../../components/Icon';
import Table from '../../components/Table';
import ManageUsersRow from './manage-users-row';
import PageTitle from '../../components/PageTitle';
import './styles.css';

const messages = defineMessages({
  addUser: {
    id: 'user.add.caption',
    defaultMessage: 'Add New User',
  },
  showActiveUsers: {
    id: 'user.show.active.users',
    defaultMessage: 'Show Active Users',
  },
  showDisabledUsers: {
    id: 'user.show.disabled.users',
    defaultMessage: 'Show Disabled Users',
  },
  manageUsers: {
    id: 'manage.users.caption',
    defaultMessage: 'Users',
  },
  manageSettings: {
    id: 'manage.users.manage-settings',
    defaultMessage: 'Click the arrow to the right of any existing user to manage their settings and permissions.',
  },
  firstName: {
    id: 'manage.users.header.first-name',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'manage.users.header.last-name',
    defaultMessage: 'Last Name',
  },
  email: {
    id: 'manage.users.header.email',
    defaultMessage: 'Email',
  },
  role: {
    id: 'manage.users.header.role',
    defaultMessage: 'Role',
  },
  lastLogin: {
    id: 'manage.users.header.last-login',
    defaultMessage: 'Last Login',
  },
});

const initialLockStatus = {
  locked: false,
  invalidAttempts: [],
};
const newUserLink = '/manageUsers/add-user';

class ManageUsersPage extends React.PureComponent {
  static propTypes = {
    node: PropTypes.object,
    updateHEAccountMutation: PropTypes.func.isRequired,
  };

  state = {
    showDisabledUsers: false,
  };

  updateHEAccount(changes) {
    const { updateHEAccountMutation } = this.props;
    return updateHEAccountMutation(changes);
  }

  changeActiveStatus = (user) => {
    const { node: heAccount } = this.props;
    const changes = {
      variables: {
        input: {
          heAccount: {
            id: heAccount.id,
            heUser: {
              id: user.id,
              active: !user.active,
            },
          },
        },
      },
    };
    return this.updateHEAccount(changes);
  };

  unlockUser = (user) => {
    const { node: heAccount } = this.props;
    const changes = {
      variables: {
        input: {
          heAccount: {
            id: heAccount.id,
            heUser: {
              id: user.id,
              lockStatus: JSON.stringify(initialLockStatus),
            },
          },
        },
      },
    };
    return this.updateHEAccount(changes);
  };

  reInviteUser = (user) => {
    const { node: heAccount } = this.props;
    const changes = {
      variables: {
        input: {
          heAccount: {
            id: heAccount.id,
            heUser: {
              id: user.id,
              recreate: true,
            },
          },
        },
      },
    };
    return this.updateHEAccount(changes);
  };

  toggleShowDisabledUsers = () => {
    this.setState((prevState) => ({ showDisabledUsers: !prevState.showDisabledUsers }));
  };

  render() {
    const { node: heAccount } = this.props;
    let { userList } = heAccount;
    const { showDisabledUsers } = this.state;
    const primaryUserId = get(heAccount.primaryUser, 'id');

    const disabledUsersExist = userList && userList.some((u) => !u.active);
    userList = userList.filter((user) => user.active !== showDisabledUsers);

    if (userList.length > 0) {
      userList = [...userList];
      const primaryUser = userList.find((user) => user.id === primaryUserId);
      const userListNoPrimary = userList.filter((user) => user.id !== primaryUserId);
      userList = userListNoPrimary.sort((a, b) =>
        (lowerCase(a.lastName) < lowerCase(b.lastName) ? -1 : 1));
      if (primaryUser) userList.unshift(primaryUser);
    }

    const buttonDataCy = showDisabledUsers ? 'manage-users-show-active-users-button' : 'manage-users-show-disabled-users-button';
    return (
      <div styleName="users-body">
        <PageTitle subTitle="Manage Users" title="Settings" category="Intersect" />
        <Header as="h2" data-cy="manage-users-heading-title" styleName="user-title" id="right-side-heading">
          <FormattedMessage {...messages.manageUsers} />
        </Header>
        <span>
          <Button as={Link} primary floated="right" to={newUserLink}>
            <FormattedMessage {...messages.addUser} />
          </Button>
          {disabledUsersExist &&
          (
            <Button floated="right" onClick={this.toggleShowDisabledUsers} data-cy={buttonDataCy}>
              { showDisabledUsers ?
                <FormattedMessage {...messages.showActiveUsers} /> :
                <FormattedMessage {...messages.showDisabledUsers} />}
            </Button>
          )}
        </span>
        <Table basic="very" styleName="table-margin" data-cy="manage-users-table">
          <caption styleName="hidden"><FormattedMessage {...messages.manageUsers} /></caption>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>
                <FormattedMessage {...messages.firstName} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <FormattedMessage {...messages.lastName} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <FormattedMessage {...messages.email} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <FormattedMessage {...messages.role} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <FormattedMessage {...messages.lastLogin} />
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body data-cy="manage-users-table-body">
            {userList.map((user) => (
              <ManageUsersRow
                user={user}
                key={user.id}
                isPrimary={user.id === primaryUserId}
                heAccountId={heAccount.id}
                onStatusChange={this.changeActiveStatus}
                onUnlock={this.unlockUser}
                onReInviteUser={this.reInviteUser}
              />
            ))}
          </Table.Body>
        </Table>
        <div>
          <Icon name="star" color="yellow" />
          = Primary User, which is the main account owner for Intersect.
        </div>
      </div>
    );
  }
}

export default ManageUsersPage;
